<template>
    <div
        class="message-history-list"
        v-if="messagesIn">
        <div
            class="message-item"
            v-for="message in orderedMessages"
            :class="[
                message.user_id === $authUser.id ? 'left' : 'right',
                { 'admin-message': message.message_type === 'admin' },
            ]"
            :key="message.id">
            <div class="user-info">
                <div class="credentials">
                    <i
                        class="fas fa-user-lock icon"
                        v-if="message.message_type === 'admin'"
                        :id="`admin-message-icon-${message.id}`"></i>
                    {{ message.user?.full_name }}
                    <b-tooltip
                        :target="`admin-message-icon-${message.id}`"
                        :title="$t('common.message.edit_message')"></b-tooltip>
                </div>
                <div class="date">{{ message.created_at | date_time_om }}</div>
                <div
                    class="manage-message"
                    v-if="canEdit(message) && !widgetMode">
                    <b-dropdown
                        class="ellipsis-nav message-history-nav"
                        variant="primary"
                        no-caret
                        right
                        text="Right align"
                        toggle-class="message-history-context-menu">
                        <template #button-content>
                            <i class="fas fa-ellipsis-v fa-fw"></i>
                        </template>
                        <b-dropdown-item
                            class="edit-message"
                            @click.prevent.stop="editMessage(message)">
                            <i class="btn-edit"></i>{{ $t('common.message.edit_message') }}
                        </b-dropdown-item>
                        <b-dropdown-item
                            class="remove-message"
                            @click.prevent.stop="removeMessage(message.id)">
                            <i class="btn-remove"></i>{{ $t('common.message.delete_message') }}
                        </b-dropdown-item>
                    </b-dropdown>
                </div>
            </div>
            <div class="message-block">
                <div
                    class="subject"
                    v-if="message.subject !== '' && message.subject !== null">
                    {{ message.subject }}
                </div>
                <div
                    class="textmessage"
                    v-if="hasContentToPreview(message.message, 50)"
                    v-html="message.message"></div>
                <design-2021-apprentice-display-html-component
                    class="textmessage"
                    v-else
                    :html-content="message.message">
                </design-2021-apprentice-display-html-component>
                <div class="avatar">
                    <b-avatar
                        variant="primary"
                        size="3rem"
                        :src="
                            message.user.profile_image_thumb_path
                                ? message.user.profile_image_thumb_path
                                : '/images/default_avatar_profile.jpg'
                        ">
                    </b-avatar>
                </div>
            </div>
        </div>
        <design-2021-group-show-edit-message-modal-component
            ref="edit-message-modal"
            @update-message="onUpdate($event)"></design-2021-group-show-edit-message-modal-component>
    </div>
</template>

<script>
export default {
    name: 'design-2021-apprentice-message-history-component',
    props: {
        messagesIn: Array,
        messageType: String,
        widgetMode: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        orderedMessages() {
            // Descending by date
            return this.messagesIn.sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at));
        },
    },

    methods: {
        hasContentToPreview(text, maxChar) {
            const previewText = this.$options.filters.editor_text_preview(text, maxChar);
            return previewText.split(' ').length > maxChar;
        },

        getRemainingText(messageText, maxChar) {
            const rawcontent = this.$options.filters.editor_text_preview(messageText);
            return rawcontent.split(' ').splice(maxChar).join(' ');
        },

        canEdit(message) {
            if (this.messageType === 'project') {
                if (
                    message.user_id === this.$authUser.id &&
                    !this.isStudent &&
                    message.model_type === 'App\\Models\\Project'
                ) {
                    return true;
                }
            } else if (this.messageType === 'group') {
                if (message.model_type == 'App\\Models\\Group') {
                    if (message.user_id === this.$authUser.id && !this.isStudent) {
                        return true;
                    }
                }
            }

            return false;
        },

        removeMessage(id) {
            this.alert
                .confirm(this.$t('common.message.delete_message'), this.$t('common.message.confirm_delete_message'))
                .then((confirm) => {
                    if (!confirm) {
                        return;
                    }

                    this.alert.saving();
                    if (this.messageType === 'project') {
                        this.$emit('remove-project-message', id);
                    } else if (this.messageType === 'group') {
                        this.$emit('remove-group-message', id);
                    }
                });
        },

        editMessage(message) {
            this.$refs['edit-message-modal'].show(message);
        },

        onUpdate(messageContent) {
            this.alert.saving();

            const updateMessage = {
                id: messageContent.id,
                message: messageContent.message,
                type: this.messageType,
            };

            if (this.messageType === 'project') {
                this.$emit('update-project-message', updateMessage);
            } else if (this.messageType === 'group') {
                this.$emit('update-group-message', updateMessage);
            }
        },
    },
};
</script>

<style scoped lang="scss">
.message-history-list {
    padding-right: map-get($grid-gutter-spacing, 'ten');
    max-height: 600px;
    min-height: 150px;
    overflow-y: auto;
}

.message-item {
    font-size: $default-font-size;

    &:not(:last-child) {
        margin-bottom: map-get($grid-gutter-spacing, 'fifteen');
    }

    .user-info {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: space-between;
        margin-bottom: map-get($grid-gutter-spacing, 'eight');
        font-weight: $font-weight-bold;

        .credentials {
            font-family: $headings-font-family;
            flex: 0 1 calc(100% - 35px);
        }

        .date {
            color: $text-muted-color;
            margin-left: auto;
            order: 3;
            text-align: right;
        }

        .manage-message ::v-deep {
            display: flex;
            align-items: center;
            margin-left: map-get($grid-gutter-spacing, 'fifteen');

            .btn {
                width: 20px;
                padding: 0;

                &:before {
                    margin-right: 0;
                }
            }

            .btn-edit-message {
                margin-right: map-get($grid-gutter-spacing, 'ten');
            }
        }
    }

    .message-block {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        align-content: center;
        justify-content: flex-start;

        .subject {
            font-size: $base-font-size;
            font-weight: $font-weight-bold;
            line-height: 1.5;
            color: var(--primary);
        }

        .textmessage {
            display: flex;
            flex-direction: column;
            flex-grow: 1;
            margin-right: map-get($grid-padding-spacing, 'twenty');
            padding: map-get($grid-gutter-spacing, 'ten');
            background-color: $secondary-background-color;
            position: relative;
            border-top-left-radius: map-get($grid-gutter-spacing, 'ten');
            border-bottom-left-radius: map-get($grid-gutter-spacing, 'ten');
            border-bottom-right-radius: map-get($grid-gutter-spacing, 'ten');

            .btn-show-remaining-message {
                display: flex;
                margin-top: map-get($grid-gutter-spacing, 'eight');

                &.not-collapsed {
                    display: none;
                }
            }

            .btn-show-preview-message {
                width: 100%;
                margin-top: map-get($grid-gutter-spacing, 'eight');
            }

            &:before {
                content: '';
                position: absolute;
                display: block;
                top: 0;
            }

            p:last-child {
                margin-bottom: 0;
            }

            .btn {
                font-size: 0.75rem;
                flex-grow: 1;
                justify-content: flex-end;
            }
        }

        .avatar {
            flex: 0 0 auto;

            .b-avatar {
                background-color: transparent;
            }
        }
    }

    &.left {
        .message-block {
            flex-direction: row-reverse;

            .subject {
                flex: 0 1 calc(100% - 68px);
            }

            .textmessage {
                margin-right: 0;
                margin-left: map-get($grid-padding-spacing, 'twenty');
                border-top-left-radius: 0;
                border-top-right-radius: map-get($grid-gutter-spacing, 'ten');

                &:before {
                    border-top: 1em solid $secondary-background-color;
                    border-left: 1em solid transparent;
                    left: -13px;
                }
            }
        }
    }

    &.right {
        .message-block {
            .subject {
                flex: 1 0 100%;
            }

            .textmessage {
                &:before {
                    border-top: 1em solid $secondary-background-color;
                    border-right: 1em solid transparent;
                    right: -13px;
                }
            }
        }
    }

    &.admin-message {
        background-color: $secondary-background-color;
        border: 1px solid var(--primary);
        border-radius: $border-radius-content;
        padding: map-get($grid-gutter-spacing, 'ten');

        .icon {
            color: var(--primary);
            font-size: $base-font-size;
            margin-right: map-get($grid-gutter-spacing, 'eight');
        }

        .textmessage {
            background-color: $white;
        }

        &.left,
        &.right {
            div.textmessage {
                &:before {
                    border-top: 1em solid $white;
                }
            }
        }
    }
}

.message-history-nav ::v-deep {
    .dropdown-menu {
        border: 1px solid $border-color;

        .edit-message {
            i {
                color: var(--primary);
            }
        }

        .edit-message,
        .remove-message {
            .dropdown-item {
                display: flex;
                align-items: center;

                i {
                    width: 20px;
                    margin-right: map-get($grid-gutter-spacing, 'eight');

                    &:before {
                        margin-right: 0;
                    }
                }
            }
        }
    }
}

@media (min-width: map-get($grid-breakpoints, 'md')) {
    .message-item {
        .user-info {
            .credentials {
                margin-right: 0;
                flex: 0 1 auto;
            }

            .date {
                order: 2;
            }

            .manage-message {
                order: 3;
            }
        }
    }
}
</style>
